import * as React from "react";
import { Doc } from "@repo/convex/convex/_generated/dataModel";
import { domain, pictureUrls, siteColor, siteIconUrls } from "./format.ts";

type PictureItem = Pick<Doc<"items">, "url" | "headerImageUrl" | "siteIconUrl">;

/**
 * Tries each source in turn and shows `fallback` once they've all failed (or there are none).
 * Lots of sites have no /favicon.ico, so a miss here is normal rather than an error.
 */
export const FallbackImage: React.FC<{
  srcs: string[];
  fallback: React.ReactNode;
  className?: string;
  alt?: string;
}> = ({ srcs, fallback, className, alt = "" }) => {
  const [index, setIndex] = React.useState(0);
  const key = srcs.join("|");
  React.useEffect(() => setIndex(0), [key]);
  if (index >= srcs.length) return <>{fallback}</>;
  return (
    <img
      key={srcs[index]}
      src={srcs[index]}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setIndex((i) => i + 1)}
    />
  );
};

/**
 * The site's own icon, small, beside its domain: the one its page names, then its /favicon.ico,
 * then its initial if it has neither.
 */
export const SiteIcon: React.FC<{ item: PictureItem; size?: number }> = ({ item, size = 15 }) => {
  const letter = domain(item.url).charAt(0).toUpperCase() || "•";
  return (
    <span
      className="site-icon"
      style={{ width: size, height: size, borderRadius: size * 0.24 }}
      aria-hidden
    >
      <FallbackImage
        srcs={siteIconUrls(item)}
        fallback={
          <span className="site-icon-letter" style={{ fontSize: size * 0.62 }}>
            {letter}
          </span>
        }
      />
    </span>
  );
};

/**
 * The small square picture at the start of an article's row. Without one, the site's icon on a
 * wash of its colour.
 */
export const ItemThumbnail: React.FC<{ item: PictureItem; size?: number }> = ({
  item,
  size = 64,
}) => (
  <span className="item-thumb" style={{ width: size, height: size }} aria-hidden>
    <FallbackImage
      srcs={pictureUrls(item)}
      fallback={
        <span className="item-thumb-empty">
          <span
            className="item-thumb-wash"
            style={{ background: siteColor(domain(item.url)) }}
          />
          <SiteIcon item={item} size={Math.round(size * 0.38)} />
        </span>
      }
    />
  </span>
);

/** An item's picture, filling its frame, or plain paper if it hasn't one. */
export const ItemPicture: React.FC<{ item: PictureItem; className?: string }> = ({
  item,
  className,
}) => (
  <FallbackImage
    srcs={pictureUrls(item)}
    className={className}
    fallback={<span className={`${className ?? ""} item-picture-empty`} />}
  />
);

/** The round play mark in the middle of a video's picture. */
export const PlayMark: React.FC = () => (
  <span className="play-mark" aria-hidden>
    <svg viewBox="0 0 24 24" width="18" height="18">
      <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
    </svg>
  </span>
);

/** X's mark, drawn rather than fetched: white on near-black, like its own icon. */
export const XMark: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <span className="x-mark" style={{ width: size, height: size }} aria-hidden>
    <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62}>
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  </span>
);
